import { css, CSSResult } from 'lit'
import { csColorBackground, csColorBorder } from '@cloudsuite/mosaic-shared/styles/colors.style.js'
import { csBorder, csTransition } from '@cloudsuite/mosaic-shared/styles/globals.style.js'
import { csSpacing } from '@cloudsuite/mosaic-shared/styles/spacing.style.js'


export const cseAccordion = {
  headerGap: css`var(--cse-accordion-header-gap, ${csSpacing.spacing05})`,
  iconRotateOpen: css`var(--cse-accordion-icon-rotate-open, -180deg)`,

  defaultHeaderBackgroundColor: css`var(--cse-accordion-default-header-background-color, ${csColorBackground.secondary})`,
  defaultHeaderBorderTopRadius: css`var(--cse-accordion-default-header-border-top-radius, ${csBorder.radiusMinimal})`,
  defaultHeaderBorderBottomRadius: css`var(--cse-accordion-default-header-border-bottom-radius, 0)`,
  defaultHeaderPaddingBlock: css`var(--cse-accordion-default-header-padding-y, ${csSpacing.spacing05})`,
  defaultHeaderPaddingInline: css`var(--cse-accordion-default-header-padding-x, ${csSpacing.spacing06})`,

  defaultContentBackgroundColor: css`var(--cse-accordion-default-content-background-color, ${csColorBackground.primary})`,
  defaultContentPaddingBlock: css`var(--cse-accordion-default-content-padding-y, ${csSpacing.spacing06})`,
  defaultContentPaddingInline: css`var(--cse-accordion-default-content-padding-x, ${csSpacing.spacing06})`,

  panelGap: css`var(--cse-accordion-panel-gap, ${csSpacing.spacing04})`,
  panelHeaderPaddingBlock: css`var(--cse-accordion-panel-header-padding-y, ${csSpacing.spacing05})`,
  panelContentBorderBottomColor: css`var(--cse-accordion-panel-border-bottom-color, ${csColorBorder.light})`,
  panelContentBorderBottomWidth: css`var(--cse-accordion-panel-border-bottom-width, ${csBorder.width})`,
  panelContentPaddingBottom: css`var(--cse-accordion-panel-content-padding-bottom, ${csSpacing.spacing06})`,
  panelContentPaddingTop: css`var(--cse-accordion-panel-content-padding-top, ${csSpacing.spacing04})`,

  simpleHeaderPaddingBlock: css`var(--cse-accordion-simple-header-padding-y, ${csSpacing.spacing05})`,
  simpleContentPaddingBottom: css`var(--cse-accordion-simple-content-padding-bottom, 0)`,
  simpleContentPaddingTop: css`var(--cse-accordion-simple-content-padding-top, ${csSpacing.spacing04})`,

  groupGap: css`var(--cse-accordion-group-gap, ${csSpacing.spacing04})`
}


export const style: CSSResult = css`
  :host {
    interpolate-size: allow-keywords;
  }

  summary {
    list-style: none;
  }
  
  summary::-webkit-details-marker {
    display: none;
  }


  /* Ensure accordion maintains full width */
  .cse-accordion {
    width: 100%; 
    display: block;
    min-width: 100%;
  }

  /* Force full width on all accordion elements */
  :host {
    width: 100%;
    display: block;
  }

  .header {
    align-items: start;
    display: flex;
    justify-content: space-between;
    gap: ${cseAccordion.headerGap};
    cursor: pointer;
    width: 100%;
  }

  .header slot[name="title"]::slotted(*) {
    flex: 1;
    min-width: 0;
  }

  .icon,
  ::slotted([slot="icon-closed"]),
  ::slotted([slot="icon-open"]) {
    transform: rotate(0);
    transition-duration: ${csTransition.duration};
    transition-property: transform;
    transition-timing-function: ${csTransition.timingFunction};
  }

  details[open] .icon,
  .cse-accordion.open .icon,
  details[open] ::slotted([slot="icon-closed"]),
  .cse-accordion.open ::slotted([slot="icon-closed"]),
  details[open] ::slotted([slot="icon-open"]),
  .cse-accordion.open ::slotted([slot="icon-open"]) {
    transform: rotate(${cseAccordion.iconRotateOpen});
  }

  slot.hidden {
    display: none;
  }

  .icon.no-rotate {
    transform: none !important;
  }



  .content > slot[name="content"] {
    display: block;
    width: 100%;
  }

  .variant-default .header {
    background-color: ${cseAccordion.defaultHeaderBackgroundColor};
    border-top-left-radius: ${cseAccordion.defaultHeaderBorderTopRadius};
    border-top-right-radius: ${cseAccordion.defaultHeaderBorderTopRadius};
    border-bottom-left-radius: ${cseAccordion.defaultHeaderBorderBottomRadius};
    border-bottom-right-radius: ${cseAccordion.defaultHeaderBorderBottomRadius};
    padding-block: ${cseAccordion.defaultHeaderPaddingBlock};
    padding-inline: ${cseAccordion.defaultHeaderPaddingInline};
    width: 100%;
    border-width: 0;
  }

  .variant-default .content {
    background-color: ${cseAccordion.defaultContentBackgroundColor};
    padding-block: ${cseAccordion.defaultContentPaddingBlock};
    padding-inline: ${cseAccordion.defaultContentPaddingInline};
  }

  details[open].variant-default .content {
    padding-block: ${cseAccordion.defaultContentPaddingBlock};
  }

  .variant-panel {
    display: flex;
    flex-direction: column;
  }

  .variant-panel .header {
    padding-block: ${cseAccordion.panelHeaderPaddingBlock};
    padding-inline: 0;
    background: none;
    border-width: 0;
  }

  .variant-panel .content {
    padding-block: 0;
    padding-inline: 0;
  }
  
  details[open].variant-panel .content,
  .cse-accordion.open.variant-panel .content {
    padding-top: ${cseAccordion.panelContentPaddingTop};
    padding-bottom: ${cseAccordion.panelContentPaddingBottom};
  }
  
  .cse-accordion.variant-panel {
    border-bottom-style: solid;
    border-bottom-color: ${cseAccordion.panelContentBorderBottomColor};
    border-bottom-width: ${cseAccordion.panelContentBorderBottomWidth};
  }

  .variant-simple .header {
    align-items: center;
    justify-content: flex-start;
    padding-block: ${cseAccordion.simpleHeaderPaddingBlock};
  }

  .variant-simple .header slot[name="title"]::slotted(*) {
    flex: 0 1 auto;
  }

  .variant-simple .content {
    padding-top: 0;
    padding-bottom: 0;
  }

  details[open].variant-simple .content {
    padding-top: ${cseAccordion.simpleContentPaddingTop};
    padding-bottom: ${cseAccordion.simpleContentPaddingBottom};
  }

  .cse-accordion-group {
    display: flex;
    flex-direction: column;
    gap: ${cseAccordion.groupGap};
    width: 100%;
  }

  details::details-content {
    height: 0;
    overflow: clip;
    transition: ${csTransition.duration} ${csTransition.timingFunction};
    transition-behavior: allow-discrete;
    transition-property: height, content-visibility;
  }

  details[open]::details-content {
    height: auto;
  }
`
